// CommunityComposer.tsx — community post composer (signed-out → SignInCta).
import { useState } from 'react';
import type { ChangeEvent } from 'react';
import styled from 'styled-components';
import { ConsolePanel, Button, Avatar } from '@/ui';
import { SignInCta } from '@/components/SignInCta';
import { useSession } from '@/hooks';

const Wrap = styled(ConsolePanel)`display: flex; flex-direction: column; gap: 12px;`;
const Body = styled.div`display: flex; gap: 12px; align-items: flex-start;`;
const Area = styled.textarea`
  flex: 1; background: transparent; border: none; border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  min-height: 56px; resize: none; color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.md}; outline: none;
  &::placeholder { color: ${({ theme }) => theme.color.textMuted}; }
  &:focus-visible { border-bottom-color: ${({ theme }) => theme.color.accentCyan}; }
`;
const Row = styled.div`display: flex; justify-content: flex-end;`;

export interface CommunityComposerProps {
  communityName: string;
  onSubmit: (body: string) => Promise<void> | void;
}

export function CommunityComposer({ communityName, onSubmit }: CommunityComposerProps) {
  const { isAuthenticated, currentUser } = useSession();
  const [value, setValue] = useState('');
  if (!isAuthenticated) return <SignInCta message={`Sign in to post in ${communityName}.`} />;
  const trimmed = value.trim();
  async function handle() {
    if (!trimmed) return;
    try {
      await onSubmit(trimmed);
      setValue('');
    } catch {
      // The caller has already said what went wrong. Keep the text: a failed post must
      // never cost the member what they wrote.
    }
  }
  return (
    <Wrap>
      <Body>
        <Avatar name={currentUser?.name ?? 'User'} size={40} />
        <Area
          value={value}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
          placeholder={`Start a post in ${communityName}`}
          aria-label={`Post in ${communityName}`}
        />
      </Body>
      <Row><Button variant="primary" size="sm" disabled={!trimmed} onClick={() => void handle()}>Post</Button></Row>
    </Wrap>
  );
}
